import { useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  QueueJoinRequest,
  QueueEntryResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/** The court-queue modes a player can pick from the hub. These are the REAL
 * backend enum values — NOT the demo copy ("casual"/"singles"/"doubles"). */
export type QueueMode = QueueJoinRequest["mode"];

interface QueueJoinAttempt {
  mode: QueueMode;
  request: QueueJoinRequest;
}

const QUEUE_JOIN_ATTEMPT_KEY_PREFIX = "bw:queue-join-attempt:v1";
const QUEUE_MODES: readonly QueueMode[] = [
  "normal",
  "ranked_singles",
  "ranked_doubles",
];
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function attemptStorageKey(
  facilityId: string,
  userId: string,
  mode: QueueMode,
) {
  return `${QUEUE_JOIN_ATTEMPT_KEY_PREFIX}:${encodeURIComponent(userId)}:${facilityId}:${mode}`;
}

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function removeStoredAttempt(
  facilityId: string,
  userId: string,
  mode: QueueMode,
) {
  try {
    getSessionStorage()?.removeItem(
      attemptStorageKey(facilityId, userId, mode),
    );
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function readStoredAttempt(
  facilityId: string,
  userId: string,
  mode: QueueMode,
): QueueJoinAttempt | null {
  const storage = getSessionStorage();
  if (storage === null) return null;
  const key = attemptStorageKey(facilityId, userId, mode);

  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<QueueJoinAttempt>;
    const request = parsed.request as Partial<QueueJoinRequest> | undefined;
    if (
      parsed.mode !== mode ||
      request?.facility_id !== facilityId ||
      request.user_id !== userId ||
      request.mode !== mode ||
      request.exclusive !== false ||
      typeof request.group_id !== "string" ||
      !UUID_PATTERN.test(request.group_id) ||
      typeof request.idempotency_key !== "string" ||
      !UUID_PATTERN.test(request.idempotency_key)
    ) {
      removeStoredAttempt(facilityId, userId, mode);
      return null;
    }
    return {
      mode,
      request: request as QueueJoinRequest,
    };
  } catch {
    removeStoredAttempt(facilityId, userId, mode);
    return null;
  }
}

function storeAttempt(attempt: QueueJoinAttempt) {
  try {
    getSessionStorage()?.setItem(
      attemptStorageKey(
        attempt.request.facility_id,
        attempt.request.user_id,
        attempt.mode,
      ),
      JSON.stringify(attempt),
    );
  } catch {
    // The in-memory ref still protects retries while this component is mounted.
  }
}

/**
 * Join the court queue (POST /queue).
 *
 * For an individual player the backend derives `user_id` from the token and we
 * mint one immutable operation snapshot for the attempt. Its `group_id` and
 * `idempotency_key` survive ambiguous retries and page reloads so response loss
 * cannot create a second entry or assignment. A different mode starts a
 * different operation.
 *
 * The mutation does NOT swallow errors — the 409 gating signals (no active
 * session / queue closed / ranked needs staff) surface on `mutation.error` and
 * the hub maps them to honest inline states (never a blank crash).
 */
export function useJoinQueue(facilityId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const attemptRef = useRef<QueueJoinAttempt | null>(null);

  const clearAttempt = useCallback(
    (mode?: QueueMode) => {
      const current = attemptRef.current;
      if (current !== null && (mode === undefined || current.mode === mode)) {
        removeStoredAttempt(
          current.request.facility_id,
          current.request.user_id,
          current.mode,
        );
        attemptRef.current = null;
      }

      const userId = user?.uid ?? "";
      const modes = mode === undefined ? QUEUE_MODES : [mode];
      for (const storedMode of modes) {
        removeStoredAttempt(facilityId, userId, storedMode);
      }
    },
    [facilityId, user?.uid],
  );

  const mutation = useMutation<QueueEntryResponse, unknown, QueueMode>({
    mutationFn: (mode: QueueMode) => {
      const userId = user?.uid ?? "";
      if (
        attemptRef.current?.mode !== mode ||
        attemptRef.current.request.facility_id !== facilityId ||
        attemptRef.current.request.user_id !== userId
      ) {
        attemptRef.current =
          readStoredAttempt(facilityId, userId, mode) ??
          ({
            mode,
            request: {
              facility_id: facilityId,
              // Solo player → one group of one for the whole operation.
              group_id: crypto.randomUUID(),
              idempotency_key: crypto.randomUUID(),
              // The backend overwrites user_id from the auth token.
              user_id: userId,
              exclusive: false,
              mode,
            },
          } satisfies QueueJoinAttempt);
        storeAttempt(attemptRef.current);
      }
      return queueApi.join(attemptRef.current.request);
    },
    onSuccess: (_entry, mode) => {
      clearAttempt(mode);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.myQueueEntry(facilityId),
        refetchType: "active",
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.session(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.facility(facilityId),
        refetchType: "active",
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dropin.queueStats(facilityId),
        refetchType: "active",
      });
    },
  });

  return { ...mutation, clearAttempt };
}
